import { router, useForm } from "@inertiajs/react"
import { useState } from "react"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"

/** Start a bid from the list: pick a production-builder community without
 *  one, or — the bid-first door — create the community (and its builder)
 *  right here. One request makes builder → community → bid and lands on
 *  the community page, where the plan set and plans come next. */
export function NewBidDialog({
  communities,
  builders,
}: {
  communities: { id: number; name: string; customer_name: string }[]
  builders: { id: number; name: string }[]
}) {
  const [open, setOpen] = useState(false)
  // With no biddable communities left, open straight into New-community
  // mode — the old empty state was a dead end.
  const [communityChoice, setCommunityChoice] = useState(communities.length === 0 ? "new" : "")
  const [submitting, setSubmitting] = useState(false)

  const form = useForm({
    customer_choice: "",
    customer_name: "",
    name: "",
    locality: "",
    start_bid: true,
  })
  const creating = communityChoice === "new"
  const creatingBuilder = form.data.customer_choice === "new"
  const errors = form.errors as Partial<Record<keyof typeof form.data, string>>

  const ready = creating
    ? form.data.name.trim() !== "" &&
      (creatingBuilder ? form.data.customer_name.trim() !== "" : form.data.customer_choice !== "")
    : communityChoice !== ""

  const start = () => {
    if (!ready) return
    if (creating) {
      form.post("/communities")
    } else {
      setSubmitting(true)
      router.post(`/communities/${communityChoice}/bid`, {}, {
        onFinish: () => setSubmitting(false),
      })
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="new-bid">＋ New bid</Button>
      </DialogTrigger>
      <DialogContent size="sm" data-testid="new-bid-dialog">
        <DialogHeader>
          <DialogTitle>New bid</DialogTitle>
          <DialogDescription>
            One bid per community — pricing, lineup, and plan quantities all live there. Production
            builders only; retail and custom jobs are bid in FloorCore.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <label htmlFor="new-bid-community">Community</label>
            <Select
              id="new-bid-community"
              value={communityChoice}
              onChange={(e) => setCommunityChoice(e.target.value)}
              data-testid="new-bid-community-select"
            >
              {communities.length > 0 && <option value="">Pick a community…</option>}
              {communities.map((community) => (
                <option key={community.id} value={community.id}>
                  {community.name} — {community.customer_name}
                </option>
              ))}
              <option value="new">＋ New community…</option>
            </Select>
          </div>

          {creating && (
            <>
              <div className="flex flex-col gap-1">
                <label htmlFor="new-bid-builder">Builder</label>
                <Select
                  id="new-bid-builder"
                  value={form.data.customer_choice}
                  onChange={(e) => form.setData("customer_choice", e.target.value)}
                  aria-invalid={!!errors.customer_choice}
                  data-testid="new-bid-builder-select"
                >
                  <option value="">Pick a builder…</option>
                  {builders.map((builder) => (
                    <option key={builder.id} value={builder.id}>
                      {builder.name}
                    </option>
                  ))}
                  <option value="new">＋ New builder…</option>
                </Select>
                {errors.customer_choice && (
                  <p className="text-xs text-danger-display">{errors.customer_choice}</p>
                )}
              </div>

              {creatingBuilder && (
                <div className="flex flex-col gap-1">
                  <label htmlFor="new-bid-builder-name">Builder name</label>
                  <Input
                    id="new-bid-builder-name"
                    type="text"
                    required
                    placeholder="Pulte"
                    aria-invalid={!!errors.customer_name}
                    value={form.data.customer_name}
                    onChange={(e) => form.setData("customer_name", e.target.value)}
                    data-testid="new-bid-builder-name"
                  />
                  {errors.customer_name && (
                    <p className="text-xs text-danger-display">{errors.customer_name}</p>
                  )}
                </div>
              )}

              <div className="flex flex-col gap-1">
                <label htmlFor="new-bid-community-name">Community name</label>
                <Input
                  id="new-bid-community-name"
                  type="text"
                  required
                  placeholder="Crescent Cove"
                  aria-invalid={!!errors.name}
                  value={form.data.name}
                  onChange={(e) => form.setData("name", e.target.value)}
                  data-testid="new-bid-community-name"
                />
                {errors.name && <p className="text-xs text-danger-display">{errors.name}</p>}
              </div>

              <div className="flex flex-col gap-1">
                <label htmlFor="new-bid-locality">Locality (optional)</label>
                <Input
                  id="new-bid-locality"
                  type="text"
                  placeholder="Myrtle Beach, SC"
                  value={form.data.locality}
                  onChange={(e) => form.setData("locality", e.target.value)}
                  data-testid="new-bid-locality"
                />
              </div>
            </>
          )}
        </div>
        <div className="modal-footer">
          <Button variant="ghost" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={start}
            disabled={!ready || submitting || form.processing}
            data-testid="new-bid-start"
          >
            Start bid
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
